import type {
  ApiErrorBody,
  BlockDto,
  FeedResponse,
  ItemDraft,
  ItemDetailDto,
  ItemDto,
  ItemsPage,
  MeDto,
  ParseResult,
  PlaylistCardDto,
  PlaylistDraft,
  PlaylistDto,
  ProfileDto,
  PublicUserDto,
  SearchResults,
} from './types';
import { t } from '../i18n';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let csrfToken: string | null = null;
export const setCsrfToken = (token: string | null): void => {
  csrfToken = token;
};
export const getCsrfToken = (): string | null => csrfToken;

const BASE = '/api';

type Query = Record<string, string | number | boolean | undefined | null>;

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (csrfToken && method !== 'GET') headers['x-csrf-token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'same-origin',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', t('errors.offline'));
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(response.status, error?.code ?? 'UNKNOWN', error?.message ?? t('errors.unknown'), error?.details);
  }
  return payload as T;
}

const get = <T,>(path: string, query?: Query) => request<T>('GET', withQuery(path, query));
const post = <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {});
const patch = <T,>(path: string, body: unknown) => request<T>('PATCH', path, body);
const del = <T,>(path: string) => request<T>('DELETE', path);

/** Fields the editor shows; the API only accepts the ones it knows about. */
function itemBody(draft: Partial<ItemDraft> & { url?: string }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (draft.url !== undefined) body.url = draft.url;
  if (draft.title !== undefined) body.title = draft.title;
  if (draft.description !== undefined) body.description = draft.description || null;
  if (draft.imageUrl !== undefined) body.imageUrl = draft.imageUrl || null;
  if (draft.type !== undefined) body.type = draft.type;
  if (draft.author !== undefined) body.author = draft.author || null;
  if (draft.personalNote !== undefined) body.personalNote = draft.personalNote || null;
  if (draft.status !== undefined) body.status = draft.status || null;
  if (draft.game !== undefined) body.game = draft.game;
  return body;
}

export const api = {
  auth: {
    me: () => get<{ user: MeDto; csrfToken?: string }>('/auth/me'),
    register: (input: { email: string; username: string; password: string; displayName?: string }) =>
      post<{ user: MeDto; csrfToken: string }>('/auth/register', input),
    login: (identifier: string, password: string) =>
      post<{ user: MeDto; csrfToken: string }>('/auth/login', { identifier, password }),
    logout: () => post<{ ok: boolean }>('/auth/logout'),
  },

  items: {
    list: (query: { cursor?: string; limit?: number; type?: string; status?: string; q?: string }) =>
      get<ItemsPage>('/items', query),
    parse: (url: string) => post<ParseResult>('/items/parse', { url }),
    create: (draft: ItemDraft) => post<ItemDto>('/items', itemBody(draft)),
    get: (id: string) => get<ItemDetailDto>(`/items/${id}`),
    update: (id: string, draft: Partial<ItemDraft>) => patch<ItemDto>(`/items/${id}`, itemBody(draft)),
    remove: (id: string) => del<{ ok: boolean }>(`/items/${id}`),
  },

  playlists: {
    mine: async (q?: string): Promise<PlaylistCardDto[]> =>
      (await get<{ playlists: PlaylistCardDto[] }>('/playlists', { q })).playlists,
    get: (id: string) => get<PlaylistDto>(`/playlists/${id}`),
    create: (draft: Partial<PlaylistDraft> & { title: string }) =>
      post<PlaylistDto>('/playlists', { ...draft, description: draft.description || null }),
    update: (id: string, draft: Partial<PlaylistDraft>) =>
      patch<PlaylistDto>(`/playlists/${id}`, {
        ...draft,
        ...(draft.description !== undefined ? { description: draft.description || null } : {}),
        ...(draft.coverImageUrl !== undefined ? { coverImageUrl: draft.coverImageUrl || null } : {}),
      }),
    remove: (id: string) => del<{ ok: boolean }>(`/playlists/${id}`),
    addBlock: (
      playlistId: string,
      block: { type: 'text'; content: string; position?: number } | { type: 'item'; itemId?: string; item?: Record<string, unknown>; position?: number },
    ) => post<BlockDto>(`/playlists/${playlistId}/blocks`, block),
    updateBlock: (playlistId: string, blockId: string, patchBody: { content?: string; itemId?: string }) =>
      patch<BlockDto>(`/playlists/${playlistId}/blocks/${blockId}`, patchBody),
    deleteBlock: (playlistId: string, blockId: string) =>
      del<{ ok: boolean }>(`/playlists/${playlistId}/blocks/${blockId}`),
    reorder: (playlistId: string, blocks: string[]) =>
      patch<{ ok: boolean; count: number }>(`/playlists/${playlistId}/reorder`, { blocks }),
  },

  users: {
    stats: () => get<{ items: number; playlists: number; followers: number; following: number }>('/users/me/stats'),
    updateMe: (input: { displayName?: string; username?: string; bio?: string; avatarUrl?: string }) =>
      patch<MeDto>('/users/me', input),
    changePassword: (currentPassword: string, newPassword: string) =>
      post<{ ok: boolean }>('/users/me/password', { currentPassword, newPassword }),
    profile: (username: string) => get<ProfileDto>(`/users/${username}`),
    playlists: async (username: string): Promise<PlaylistCardDto[]> =>
      (await get<{ playlists: PlaylistCardDto[] }>(`/users/${username}/playlists`)).playlists,
    followers: (username: string) => get<{ users: PublicUserDto[] }>(`/users/${username}/followers`),
    following: (username: string) => get<{ users: PublicUserDto[] }>(`/users/${username}/following`),
    follow: (id: string) => post<{ followers: number }>(`/users/${id}/follow`),
    unfollow: (id: string) => del<{ followers: number }>(`/users/${id}/follow`),
  },

  feed: () => get<FeedResponse>('/feed', { limit: 30 }),
  search: (q: string) => get<SearchResults>('/search', { q, limit: 20 }),
};
